/**
 * 식별자 관리
 *
 * Visitor ID (영구), Session ID (세션 단위) 생성 및 관리
 */

import { LocalStorageAdapter, SessionStorageAdapter, CookieAdapter } from './storage';
import { generateUUID } from '../utils/uuid';
import {
  getUtmParams,
  getPageLocation,
  getClickIdsFromUrl,
  getFbCookies,
  synthesizeFbc,
} from '../utils/url';
import type { UtmParams, ClickIds } from '../utils/url';

const VISITOR_ID_KEY = '_la_vid';
const SESSION_ID_KEY = '_la_sid';
const SESSION_LAST_ACTIVE_KEY = '_la_sla';
const USER_ID_KEY = '_la_uid';
const SESSION_START_SENT_KEY = '_la_ss';
const VISITOR_ID_EXPIRY_DAYS = 730; // 2년

/** last-touch UTM 스냅샷 영속 키 (LS + 쿠키, 30일) */
const UTM_KEY = '_la_utm';
const UTM_EXPIRY_DAYS = 30;
/**
 * last-touch 광고 클릭ID 스냅샷 영속 키 (localStorage 단독, FRD 38 §8.8).
 * 광고 매칭 식별자이므로 쿠키 미러를 두지 않는다(상시 HTTP 송신 방지).
 * fbc 신선도(클릭 캡처 시각)를 별도 보관해 합성 시 활용.
 */
const CLICK_IDS_KEY = '_la_clk';
/** 클릭ID 신선도(unix ms) 영속 키 — fbc 합성 타임스탬프 보존. */
const CLICK_TS_KEY = '_la_clk_ts';
/** Meta 클릭(fbclid) 어트리뷰션 윈도우와 동일하게 90일 보존. */
const CLICK_IDS_EXPIRY_DAYS = 90;
/** landing_page 영속 키 (sessionStorage, 세션 첫 진입 1회) */
const LANDING_PAGE_KEY = '_la_lp';

export class IdentityManager {
  private localStorage: LocalStorageAdapter;
  private sessionStorage: SessionStorageAdapter;
  private cookieAdapter: CookieAdapter;
  private sessionTimeoutMinutes: number;

  constructor(sessionTimeoutMinutes: number = 30) {
    this.localStorage = new LocalStorageAdapter();
    this.sessionStorage = new SessionStorageAdapter();
    this.cookieAdapter = new CookieAdapter();
    this.sessionTimeoutMinutes = sessionTimeoutMinutes;
  }

  getVisitorId(): string {
    const existing = this.localStorage.get(VISITOR_ID_KEY)
      ?? this.cookieAdapter.get(VISITOR_ID_KEY);

    if (existing) {
      this.localStorage.set(VISITOR_ID_KEY, existing);
      this.cookieAdapter.set(VISITOR_ID_KEY, existing, VISITOR_ID_EXPIRY_DAYS);
      return existing;
    }

    const visitorId = generateUUID();
    this.localStorage.set(VISITOR_ID_KEY, visitorId);
    this.cookieAdapter.set(VISITOR_ID_KEY, visitorId, VISITOR_ID_EXPIRY_DAYS);
    this._isNewVisitor = true;
    return visitorId;
  }

  getSessionId(): string {
    const now = Date.now();
    const lastActive = this.sessionStorage.get(SESSION_LAST_ACTIVE_KEY);
    let sessionId = this.sessionStorage.get(SESSION_ID_KEY);

    const isExpired = lastActive
      && (now - Number(lastActive)) > this.sessionTimeoutMinutes * 60 * 1000;

    if (!sessionId || isExpired) {
      sessionId = generateUUID();
      this.sessionStorage.set(SESSION_ID_KEY, sessionId);
      this.sessionStorage.remove(SESSION_START_SENT_KEY);
    }

    this.sessionStorage.set(SESSION_LAST_ACTIVE_KEY, String(now));
    return sessionId;
  }

  setUserId(userId: string): void {
    this.localStorage.set(USER_ID_KEY, userId);
  }

  getUserId(): string | undefined {
    return this.localStorage.get(USER_ID_KEY) ?? undefined;
  }

  /** session_start 이벤트를 보내야 하는지 확인 (새 세션 또는 미전송) */
  isSessionStartNeeded(): boolean {
    return !this.sessionStorage.get(SESSION_START_SENT_KEY);
  }

  /** session_start 전송 완료 마킹 */
  markSessionStartSent(): void {
    this.sessionStorage.set(SESSION_START_SENT_KEY, '1');
  }

  /** 새 방문자 여부 (현재 init 사이클에서 visitor_id를 새로 생성했는지) */
  isNewVisitor(): boolean {
    return this._isNewVisitor;
  }

  // ===== last-touch 어트리뷰션 보강 (U6) =====

  /**
   * last-touch UTM 캡처.
   *
   * 현재 URL 에 utm_* 파라미터가 하나라도 있으면 전체 UTM 스냅샷을 _la_utm 에 덮어쓴다
   * (LS + 쿠키, 30일). utm 이 전혀 없는 진입에서는 기존 last-touch 값을 보존한다.
   * init() 시점에 1회 호출.
   */
  captureUtm(): void {
    const utm = getUtmParams();
    if (Object.keys(utm).length === 0) {
      return;
    }
    try {
      const serialized = JSON.stringify(utm);
      this.localStorage.set(UTM_KEY, serialized);
      this.cookieAdapter.set(UTM_KEY, serialized, UTM_EXPIRY_DAYS);
    } catch {
      // 직렬화/저장 실패는 조용히 무시.
    }
  }

  /** 저장된 last-touch UTM 스냅샷 조회 (없으면 빈 객체). */
  getStoredUtm(): UtmParams {
    const raw = this.localStorage.get(UTM_KEY) ?? this.cookieAdapter.get(UTM_KEY);
    if (!raw) return {};
    try {
      const parsed = JSON.parse(raw) as UtmParams;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  /**
   * last-touch 광고 클릭ID 캡처 (FRD 38 §8.8).
   *
   * 현재 URL 의 fbclid/gclid/wbraid/gbraid 와 _fbc/_fbp 쿠키를 합쳐
   * 식별자가 하나라도 있으면 last-touch 스냅샷을 _la_clk 에 덮어쓴다(localStorage 단독, 90일).
   * fbc 신선도를 위해 캡처 시각(ms)도 _la_clk_ts 에 함께 보존한다.
   * 식별자가 전혀 없는 진입에서는 기존 last-touch 값을 보존한다(UTM 패턴과 동일).
   * init() 시점에 1회 호출.
   */
  captureClickIds(): void {
    const fromUrl = getClickIdsFromUrl();
    const fromCookies = getFbCookies();
    const merged: ClickIds = { ...fromUrl, ...fromCookies };

    if (Object.keys(merged).length === 0) {
      return;
    }

    try {
      const serialized = JSON.stringify(merged);
      const capturedAt = String(Date.now());
      // 광고 매칭 식별자는 localStorage 에만 저장한다.
      // getTrackingData() 로 명시 전달되므로 쿠키 미러(상시 HTTP 송신)는 불필요.
      this.localStorage.set(CLICK_IDS_KEY, serialized);
      this.localStorage.set(CLICK_TS_KEY, capturedAt);
    } catch {
      // 직렬화/저장 실패는 조용히 무시(best-effort).
    }
  }

  /**
   * 저장된 last-touch 클릭ID 스냅샷 조회.
   *
   * fbclid 가 있으면 캡처 타임스탬프(신선도)를 이용해 Meta CAPI 규격 fbc 를 합성한다
   * (`fb.1.<unix_ts>.<fbclid>`). 이미 _fbc 쿠키 값이 있으면 그 값을 우선한다.
   * 값이 없으면 빈 객체.
   */
  getStoredClickIds(): ClickIds {
    const raw = this.localStorage.get(CLICK_IDS_KEY);
    if (!raw) return {};

    let parsed: ClickIds;
    try {
      const obj = JSON.parse(raw) as ClickIds;
      parsed = obj && typeof obj === 'object' ? { ...obj } : {};
    } catch {
      return {};
    }

    // fbc 합성: 쿠키(_fbc)가 없고 fbclid 가 있을 때만 신선도 타임스탬프로 합성.
    if (!parsed.fbc && parsed.fbclid) {
      const tsRaw = this.localStorage.get(CLICK_TS_KEY);
      const tsMs = tsRaw ? Number(tsRaw) : NaN;
      parsed.fbc = synthesizeFbc(parsed.fbclid, Number.isFinite(tsMs) ? tsMs : undefined);
    }

    return parsed;
  }

  /**
   * landing_page 캡처 — 세션 첫 진입 1회만 기록(sessionStorage).
   * 이미 기록돼 있으면 보존(세션 내 최초 진입 페이지 고정).
   */
  captureLandingPage(): void {
    if (this.sessionStorage.get(LANDING_PAGE_KEY)) {
      return;
    }
    const location = getPageLocation();
    if (location) {
      this.sessionStorage.set(LANDING_PAGE_KEY, location);
    }
  }

  /** 저장된 landing_page 조회 (없으면 undefined). */
  getLandingPage(): string | undefined {
    return this.sessionStorage.get(LANDING_PAGE_KEY) ?? undefined;
  }

  private _isNewVisitor = false;
  reset(): void {
    this.localStorage.remove(VISITOR_ID_KEY);
    this.localStorage.remove(USER_ID_KEY);
    this.localStorage.remove(UTM_KEY);
    this.localStorage.remove(CLICK_IDS_KEY);
    this.localStorage.remove(CLICK_TS_KEY);
    this.sessionStorage.remove(SESSION_ID_KEY);
    this.sessionStorage.remove(SESSION_LAST_ACTIVE_KEY);
    this.sessionStorage.remove(SESSION_START_SENT_KEY);
    this.sessionStorage.remove(LANDING_PAGE_KEY);
    this.cookieAdapter.remove(VISITOR_ID_KEY);
    this.cookieAdapter.remove(UTM_KEY);
    // _la_clk / _la_clk_ts 는 localStorage 단독 저장이므로 쿠키 remove 불필요.
    // (과거 버전 잔존 쿠키 정리를 위해 best-effort 로 제거.)
    this.cookieAdapter.remove(CLICK_IDS_KEY);
    this.cookieAdapter.remove(CLICK_TS_KEY);
  }
}
